import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { trackPageView } from '../lib/analytics';
import { captureUTMParams } from '../lib/utm-manager';
import { CONSENT_CHANGE_EVENT } from '@/analytics/ga4';

export const useAnalytics = () => {
  const [location] = useLocation();
  const prevLocationRef = useRef<string | null>(null);
  const lastTrackedLocationRef = useRef<string | null>(null);
  const utmCaptured = useRef(false);

  const trackCurrentPageView = (path: string) => {
    if (lastTrackedLocationRef.current === path) return;
    trackPageView(path);
    lastTrackedLocationRef.current = path;
  };
  
  useEffect(() => {
    // Capture UTM parameters on initial load
    if (!utmCaptured.current) {
      captureUTMParams();
      utmCaptured.current = true;
    }
    
    // Track initial page view or route changes after consent
    if (prevLocationRef.current === null || location !== prevLocationRef.current) {
      const hasConsent = typeof window !== 'undefined' && localStorage.getItem('cookieConsent') === 'true';
      if (hasConsent) {
        trackCurrentPageView(location);
      }
      prevLocationRef.current = location;
    }
  }, [location]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onConsentChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ analytics: boolean }>;
      if (!customEvent.detail?.analytics) return;
      trackCurrentPageView(window.location.pathname || '/');
    };

    window.addEventListener(CONSENT_CHANGE_EVENT, onConsentChange as EventListener);
    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, onConsentChange as EventListener);
    };
  }, []);
};
